// ========================================
// SERVIÇO DE API - USANDO BACKEND LOCAL
// src/services/api.js
// ========================================

import { api } from '../config/api';

// ==================== CLIENTES ====================
export const clienteAPI = {
  // Buscar cliente por CNPJ
  buscarPorCNPJ: async (cnpj) => {
    // Remove formatação do CNPJ (pontos, barras, hífens)
    const cnpjLimpo = cnpj.replace(/\D/g, '');
    const response = await api.get(`/clientes/cnpj/${cnpjLimpo}`);
    return response.cliente;
  },

  // Buscar clientes por nome (autocomplete)
  buscarPorNome: async (termo) => {
    const response = await api.get(`/clientes/buscar-nome?q=${encodeURIComponent(termo)}`);
    return response.clientes || [];
  },

  // Buscar última compra de um cliente
  buscarUltimaCompra: async (clienteId) => {
    try {
      const response = await api.get(`/pedidos/cliente/${clienteId}/parcelados`);
      const pedidos = response.pedidos || [];
      if (pedidos.length > 0) {
        return { 
          data: pedidos[0]?.created_at || null, 
          pedidoId: pedidos[0]?.id || null 
        };
      }
      return { data: null, pedidoId: null };
    } catch (error) {
      return { data: null, pedidoId: null };
    }
  },

  // Calcular status do cliente (função local)
  calcularStatusCliente: (cliente, ultimaCompra = null) => {
    const statusFixos = ['EM_ANALISE', 'PENDENTE', 'REJEITADO', 'EXCLUIDO'];
    if (statusFixos.includes(cliente.status_cadastro)) {
      return cliente.status_cadastro === 'PENDENTE' ? 'EM_ANALISE' : cliente.status_cadastro;
    }

    if (cliente.status_cadastro === 'APROVADO' && cliente.data_aprovacao) {
      const dataAprovacao = new Date(cliente.data_aprovacao);
      const hoje = new Date();
      const diasDesdeAprovacao = Math.floor((hoje - dataAprovacao) / (1000 * 60 * 60 * 24));
      
      if (diasDesdeAprovacao <= 7) {
        return 'APROVADO';
      }
    }

    if (!ultimaCompra) {
      return 'CADASTRADO';
    }

    const dataUltimaCompra = new Date(ultimaCompra);
    const hoje = new Date();
    const diasSemCompra = Math.floor((hoje - dataUltimaCompra) / (1000 * 60 * 60 * 24));

    if (diasSemCompra <= 120) {
      return 'ATIVO';
    } else {
      return 'INATIVO';
    }
  },

  // Criar novo cliente
  criar: async (cliente, usuarioLogado = null) => {
    const response = await api.post('/clientes', cliente);
    // Retorna objeto completo para verificar se foi aprovado automaticamente
    return {
      cliente: response.cliente,
      aprovadoAutomaticamente: response.aprovadoAutomaticamente || false,
      mensagem: response.mensagem || null
    };
  },

  // Atualizar cliente
  atualizar: async (id, dados) => {
    const response = await api.put(`/clientes/${id}`, dados);
    return response.cliente;
  },

  // Atualizar observação do cliente
  atualizarObservacao: async (id, observacao_analise) => {
    const response = await api.patch(`/clientes/${id}/observacao`, { observacao_analise });
    return response.cliente;
  },

  // Listar clientes que podem fazer pedidos
  listar: async () => {
    const response = await api.get('/clientes');
    return response.clientes;
  },

  // Listar clientes por vendedor
  listarPorVendedor: async (vendedorUsername) => {
    const response = await api.get('/clientes/vendedor');
    return response.clientes;
  },

  // Listar clientes pendentes
  listarPendentes: async () => {
    const response = await api.get('/clientes/pendentes');
    return response.clientes;
  },

  // Aprovar ou rejeitar cliente
  aprovarCliente: async (id, dadosAprovacao) => {
    const response = await api.post(`/clientes/${id}/aprovar`, dadosAprovacao);
    return response.cliente;
  },

  // Excluir cliente
  excluirCliente: async (id, dadosExclusao) => {
    const response = await api.post(`/clientes/${id}/excluir`, dadosExclusao);
    return response.cliente;
  },

  // Reativar cliente
  reativarCliente: async (id, dadosReativacao) => {
    const response = await api.post(`/clientes/${id}/reativar`, dadosReativacao);
    return response.cliente;
  }
};

// ==================== PRODUTOS ====================
export const produtoAPI = {
  // Listar produtos por grupo
  listarPorGrupo: async (grupo) => {
    const response = await api.get(`/produtos/grupo/${encodeURIComponent(grupo)}`);
    return response.produtos;
  },

  // Listar todos os produtos
  listarTodos: async () => {
    const response = await api.get('/produtos');
    return response.produtos;
  },
  
  // Listar forros (produtos do grupo "Forro")
  listarForros: async () => {
    const response = await api.get('/produtos/grupo/Forro');
    return response.produtos || [];
  },
  
  // Listar acessórios (produtos dos grupos de acabamento)
  listarAcessorios: async () => {
    const response = await api.get('/produtos');
    // Filtrar apenas acessórios/acabamentos (grupos que não são "Forro")
    const produtos = response.produtos || [];
    return produtos.filter(p => p.grupo_produto !== 'Forro');
  }
};

// ==================== CUSTOS ====================
export const custoAPI = {
  // Buscar custo específico
  buscarCusto: async (familia_produto, estado, tipo_tabela) => {
    const params = new URLSearchParams({
      familia_produto,
      estado,
      tipo_tabela
    });
    const response = await api.get(`/custos/buscar?${params}`);
    return response.custo;
  },

  // Listar todos os custos
  listarTodos: async () => {
    const response = await api.get('/custos');
    return response.custos;
  }
};

// ==================== PEDIDOS ====================
export const pedidoAPI = {
  // Criar novo pedido
  criar: async (pedidoData) => {
    const response = await api.post('/pedidos', pedidoData);
    return response.pedido;
  },

  // Buscar pedidos do cliente (últimos 3 meses)
  buscarPedidosParceladosCliente: async (clienteId) => {
    const response = await api.get(`/pedidos/cliente/${clienteId}/parcelados`);
    return {
      pedidos: response.pedidos,
      quantidade: response.quantidade,
      totalValor: response.totalValor,
      // Dados de parcelados
      pedidosParcelados: response.pedidosParcelados,
      quantidadeParcelados: response.quantidadeParcelados,
      totalValorParcelados: response.totalValorParcelados,
      percentualParcelamento: response.percentualParcelamento
    };
  },

  // Buscar último pedido
  buscarUltimoPedido: async () => {
    const response = await api.get('/pedidos/ultimo');
    return response.pedido;
  },

  // Inserir itens de forro
  inserirItensForro: async (itens) => {
    const response = await api.post('/pedidos/itens-forro', itens);
    return response.itens;
  },

  // Inserir itens de acessório
  inserirItensAcessorio: async (itens) => {
    const response = await api.post('/pedidos/itens-acessorio', itens);
    return response.itens;
  },

  // Inserir itens de produtos especiais (Demais Produtos - sem cálculo de margem)
  inserirItensEspeciais: async (itens) => {
    const response = await api.post('/pedidos/itens-especiais', itens);
    return response.itens;
  },

  // Listar todos os pedidos
  listar: async () => {
    const response = await api.get('/pedidos');
    return response.pedidos;
  },

  // Buscar pedido por ID
  buscarPorId: async (id) => {
    const response = await api.get(`/pedidos/${id}`);
    return response.pedido;
  },

  // Buscar itens de um pedido
  buscarItens: async (id) => {
    const response = await api.get(`/pedidos/${id}/itens`);
    return { forros: response.forros, acessorios: response.acessorios, podeVerCustos: response.podeVerCustos };
  },

  // Atualizar pedido
  atualizar: async (id, dados) => {
    const response = await api.put(`/pedidos/${id}`, dados);
    return response.pedido;
  },

  // Deletar pedido
  deletar: async (id) => {
    const response = await api.delete(`/pedidos/${id}`);
    return response;
  },



  // Buscar pedidos por período
  buscarPorPeriodo: async (inicio, fim) => {
    const response = await api.get(`/pedidos/por-periodo?inicio=${encodeURIComponent(inicio)}&fim=${encodeURIComponent(fim)}`);
    return response.pedidos;
  },

  // Totais faturados de hoje/semana/mês (pela data de faturamento)
  buscarResumoFaturado: async () => {
    const response = await api.get('/pedidos/resumo-faturado');
    return response.resumo;
  },

  // Dados para dashboard
  buscarDadosDashboard: async (periodo) => {
    const response = await api.get(`/pedidos/dashboard?periodo=${periodo}`);
    return response.pedidos;
  },

  // Itens para dashboard análise
  buscarItensDashboard: async (periodo, tipoDocumento = 'todos') => {
    const response = await api.get(`/pedidos/itens-dashboard?periodo=${periodo}&tipoDocumento=${tipoDocumento}`);
    return { forros: response.forros, acessorios: response.acessorios };
  },

  // Buscar preço médio por m² de forros
  buscarPrecoMedioM2: async (periodo = '1ano', responsavel = '', tipoDocumento = 'todos') => {
    const params = new URLSearchParams({ periodo });
    if (responsavel) params.append('responsavel', responsavel);
    if (tipoDocumento && tipoDocumento !== 'todos') params.append('tipoDocumento', tipoDocumento);
    const response = await api.get(`/pedidos/preco-medio-m2?${params.toString()}`);
    return { totalM2: response.totalM2, totalVendas: response.totalVendas, precoMedioM2: response.precoMedioM2 };
  },

  // Atualizar responsável do pedido
  atualizarResponsavel: async (id, novoResponsavel) => {
    const response = await api.patch(`/pedidos/${id}/responsavel`, { vendedor: novoResponsavel });
    return response;
  },

  // Atualizar status/fase do pedido (Admin/Diretoria)
  atualizarStatus: async (id, novoStatus) => {
    const response = await api.patch(`/pedidos/${id}/status`, { fase_atual: novoStatus });
    return response;
  },

  // Ação rápida: Confirmar Pedido (transforma orçamento em pedido)
  confirmarPedido: async (id) => {
    const response = await api.patch(`/pedidos/${id}/confirmar-pedido`);
    return response;
  },

  // Ação rápida: Arquivar Proposta
  arquivarProposta: async (id) => {
    const response = await api.patch(`/pedidos/${id}/arquivar`);
    return response;
  },

  // Ação rápida: Cancelar Proposta (com observação obrigatória)
  cancelarProposta: async (id, observacao) => {
    const response = await api.patch(`/pedidos/${id}/cancelar`, { observacao });
    return response;
  },

  // Ação rápida: Finalizar Pedido (quando já é PEDIDO_CONFIRMADO)
  // payload: { data_faturamento: 'YYYY-MM-DD', hora_faturamento: 'HH:mm', observacao: string }
  finalizarPedido: async (id, payload = {}) => {
    const response = await api.patch(`/pedidos/${id}/finalizar-pedido`, payload);
    return response;
  },

  // Lista pedidos PEDIDO_CONFIRMADO do mesmo cliente (para faturamento em lote)
  listarConfirmadosPorCliente: async (clienteId) => {
    const response = await api.get(`/pedidos/cliente/${clienteId}/confirmados`);
    return response;
  },

  // Fatura múltiplos pedidos em lote (transação atômica)
  // payload: { pedido_ids: [], data_faturamento, hora_faturamento, observacao }
  faturarLote: async (payload = {}) => {
    const response = await api.post(`/pedidos/faturar-lote`, payload);
    return response;
  },

  // Ação rápida: Cancelar Pedido Confirmado (com observação obrigatória)
  cancelarPedidoConfirmado: async (id, observacao) => {
    const response = await api.patch(`/pedidos/${id}/cancelar-pedido`, { observacao });
    return response;
  },

  // Ação rápida: Cancelar Pedido APÓS o faturamento (Admin/Diretoria/Administrativo)
  cancelarPedidoFaturado: async (id, observacao) => {
    const response = await api.patch(`/pedidos/${id}/cancelar-pos-faturamento`, { observacao });
    return response;
  }
};

// ==================== USUÁRIOS ====================
export const usuarioAPI = {
  // Buscar usuário por ID
  buscarPorId: async (id) => {
    const response = await api.get(`/usuarios/${id}`);
    return response.usuario;
  },

  // Buscar usuário por username
  buscarPorUsername: async (username) => {
    const response = await api.get(`/usuarios/username/${encodeURIComponent(username)}`);
    return response.usuario;
  },

  // Listar usuários ativos (para selects)
  listarAtivos: async () => {
    const response = await api.get('/usuarios/ativos');
    return response.usuarios || [];
  }
};